import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import Auth from 'j-toker';
import styled from 'styled-components';
import env from 'config/constants';
import { Col, Row } from 'antd';

import {
  AntdAlert,
  AntdSelect as Select,
  AntdButton as Button,
  Divider,
  TextArea,
  Modal,
  AntdRadioGroupButton as RadioButton,
} from '@yuhuinc/ui-library/';

import { leaseCreatorColors } from 'helpers/ColorHelper';
import { getTranslatedString } from 'helpers/i18n';
import { apiGet, apiPost, apiPatch } from 'helpers/ApiHelper';
import { hellosignClient } from 'helpers/hellosignClient';
import { media, sizes } from 'helpers/MediaHelper';
import { responsive } from 'helpers/StyleHelper';
import { LeaseCreatorFormItem } from '../common/LeaseCreatorFormItem';
import { LEASE_DETAILS_PAGE } from 'helpers/DocumentTemplateHelper';
import { toFriendlyDateString } from 'helpers/Formatter';

import { LeaseCreatorPageFooter } from '../common/LeaseCreatorPageFooter';
import LeaseHeaderProgress from '../common/LeaseHeaderProgress';
import { StyledContainer } from '../common/LeaseStyles';
import LeaseReview from './LeaseReview';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

import { createHellosignUnclaimedDraft } from 'actions/landlordSignableDocActions';
import { storeLeaseReviews, storeLatestLeaseReview } from '../store/actions';

const StyledPageHeader = styled.div`
  font-size: 24px;
  line-height: 32px;
  color: 38;
  font-weight: 400;
`;

const StyledPreviewContainer = styled.div`
  background-color: white;
  width: 100%;
  margin-top: 24px;
  border: 1px solid ${leaseCreatorColors.FORM_BORDER};
  border-radius: 5px;
`;

const StyledPreview = styled.form`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-size: 24px;
  padding: 16px;
  max-width: 580px;
`;

const LeaseReviewerContainer = styled.div`
  width: 536px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  ${media.mobile`
    width: 100%;
  `};
`;

const LeaseApprovalContainer = styled.div`
  background-color: #f5f4f3;
  width: 536px;
  padding: 20px;
  margin-top: 20px;
  ${media.mobile`
    width: 100%;
    padding: 16px;
  `};
`;

const StyledTextAreaContainer = styled.div`
  margin: 8px 0;
  ${media.mobile`
    margin-top: 14px;
    margin-bottom: 24px;
  `};
`;

const StyledText = styled.p`
  margin-top: 20px;
  font-size: 1rem;
  ${media.mobile`
    font-size: 16px;
    margin-top: 16px;
    margin-bottom: 24px;
  `};
`;

const StyledTitle = styled.span`
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
`;

const StyledUnavailablePreviewInfo = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  margin-top: 16px;
`;

const LeaseReviewsContainer = styled.div`
  padding: 20px;
  ${media.mobile`
    padding: 16px;
  `};
`;

const PreviewError = styled.div`
  margin-top: 8px;
  line-height: 24px;
  font-size: 14px;
  color: ${leaseCreatorColors.ERROR_RED};
`;

const StyledRadioButton = styled(RadioButton)`
  ${media.mobile`
    margin: 14px 0;
  `};
`;

const StyledSelect = styled(Select)`
  max-width: 352px;
  ${media.mobile`
    width: 100%;
  `};
`;

const StyledAlert = styled(AntdAlert)`
  margin-top: 16px;
`;

const StyledRow = styled(Row)`
  width: 100%;
  margin-top: 16px;
`;

const StyledSelectContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 16px;
  row-gap: 24px;
  flex-wrap: wrap;
  ${media.mobile`
    width: 100%;
  `};
`;

const StyledRequiredText = styled.span`
  font-size: ${responsive.text.desktop};
  ${media.mobile`
    font-weight: 400;
    font-size: ${responsive.text.mobile};
  `};
`;

const CREATE_LEASE = 'create_lease';
const PENDING_LEASE_APPROVAL = 'pending_lease_approval';

const LEASE_APPROVAL_PENDING = 0;
const LEASE_APPROVED = 1;
const LEASE_REJECTED = 2;

const LeasePreviewPage = ({
  forward,
  back,
  goTo,
  currentPage,
  totalPages,
  leaseDetails,
  handleCancellation,
  createHellosignUnclaimedDraft,
  fetchingHellosignUnclaimedDraft,
  clientId,
  canApproveLeases,
  leaseCanBeSent,
  restartWizard,
  storeLeaseReviews,
  storeLatestLeaseReview,
  latestLeaseReview,
  leaseReviews,
}) => {
  const [approvalStatus, setApprovalStatus] = useState(LEASE_APPROVAL_PENDING);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [notice, setNotice] = useState('');
  const [reviewerId, setReviewerId] = useState(null);
  const [reviewers, setReviewers] = useState([]);
  const [application, setApplication] = useState({});
  const [previewError, setPreviewError] = useState(false);
  const [showEditModalWarning, setShowEditModalWarning] = useState(false);

  const isMobile = window.matchMedia(`(max-width: ${sizes.mobile}px)`).matches;
  const labelSize = isMobile ? responsive.text.mobile : responsive.text.desktop;

  const getReviewers = useCallback(() => {
    apiGet(Auth.getApiUrl() + `/landlords/leases/${leaseDetails.lease_id}/lease_reviewers`).then(
      (res) => {
        setReviewers(res.reviewers);
      }
    );
  }, [leaseDetails.lease_id]);

  const getLeaseReviews = useCallback(() => {
    apiGet(Auth.getApiUrl() + `/landlords/leases/${leaseDetails.lease_id}/lease_reviews`).then(
      (res) => {
        storeLeaseReviews(res.lease_reviews);
        const latestLeaseReviewStore = storeLatestLeaseReview(
          res.lease_reviews[res.lease_reviews.length - 1]
        );
        const latest = latestLeaseReviewStore.latestLeaseReview;
        setReviewerId(latest?.reviewer_id);
        if (latest && latest.status === 'rejected') {
          setNotice(
            getTranslatedString('landlordPortal.leaseCreatorV2.leasePreviewPage.leaseRejected', {
              name: latest.reviewer_name,
              date: latest.created_on,
            })
          );
        } else if (latest && latest.status === 'approved') {
          setNotice(
            getTranslatedString('landlordPortal.leaseCreatorV2.leasePreviewPage.leaseApproved')
          );
        } else if (latest && latest.status === 'pending') {
          setNotice(
            getTranslatedString('landlordPortal.leaseCreatorV2.leasePreviewPage.approvalPending')
          );
        }
      }
    );
  }, [leaseDetails.lease_id, storeLeaseReviews, storeLatestLeaseReview]);

  const loadApplication = useCallback(() => {
    apiGet(Auth.getApiUrl() + `/landlords/leases/${leaseDetails.lease_id}/application`).then(
      (res) => {
        setApplication(res.application);
      }
    );
  }, [leaseDetails.lease_id]);

  const validForm = () => {
    if (approvalStatus === LEASE_APPROVAL_PENDING) {
      setErrors({
        approvalStatus: getTranslatedString(
          'lease_creator_v2.lease_preview_page.approval_status_error'
        ),
      });
      return false;
    }
    return true;
  };

  const requestLeaseReview = () => {
    if (reviewerId) {
      setErrors({});
      apiPatch(
        Auth.getApiUrl() +
          `/landlords/leases/${leaseDetails.lease_id}/application/request_lease_review`,
        { reviewer_id: reviewerId }
      ).then((res) => {
        storeLatestLeaseReview(res.lease_review);
        getLeaseReviews();
        loadApplication();
      });
    } else {
      setErrors({
        reviewerId: getTranslatedString(
          'landlordPortal.leaseCreatorV2.leasePreviewPage.reviewerError'
        ),
      });
    }
  };

  const submitLeaseReview = () => {
    if (validForm()) {
      setErrors({});
      setNotice('');

      apiPost(Auth.getApiUrl() + `/landlords/leases/${leaseDetails.lease_id}/lease_reviews`, {
        lease_review: {
          status: approvalStatus,
          notes: notes,
        },
      }).then((res) => {
        if (approvalStatus === LEASE_APPROVED) {
          setNotice(
            getTranslatedString('landlordPortal.leaseCreatorV2.leasePreviewPage.leaseApproved')
          );
        } else if (approvalStatus === LEASE_REJECTED) {
          setNotice(
            getTranslatedString('landlordPortal.leaseCreatorV2.leasePreviewPage.leaseRejected', {
              name: res.lease_review.reviewer_name,
              date: res.lease_review.created_on,
            })
          );
        }
        storeLatestLeaseReview(res.lease_review);
        setNotes('');
        setApprovalStatus(LEASE_APPROVAL_PENDING);
        getLeaseReviews();
        loadApplication();
      });
    }
  };

  const disablePrimaryButton = () => {
    if (canApproveLeases) return false;
    if (application.status === PENDING_LEASE_APPROVAL && !canApproveLeases) return true;

    return application.status === CREATE_LEASE && !leaseCanBeSent;
  };

  const openPreview = async () => {
    try {
      setPreviewError(false);

      const response = await createHellosignUnclaimedDraft(leaseDetails.lease_id);

      hellosignClient.open(response.data.attributes.callback_url, {
        clientId,
        skipDomainVerification: env.ENV !== 'production',
      });
    } catch (err) {
      setPreviewError(true);
      console.error(err);
    }
  };

  useEffect(() => {
    getLeaseReviews();
    getReviewers();
    loadApplication();
  }, [getLeaseReviews, getReviewers, loadApplication]);

  const handleBackClick = () => {
    if (
      (!['approved'].includes(latestLeaseReview?.status) && restartWizard) ||
      ['pending', 'rejected'].includes(latestLeaseReview?.status)
    ) {
      goTo(LEASE_DETAILS_PAGE);
    } else if (
      (!['pending', 'rejected'].includes(latestLeaseReview?.status) && restartWizard) ||
      ['approved'].includes(latestLeaseReview?.status)
    ) {
      setShowEditModalWarning(true);
    } else {
      back();
    }
  };

  let alertType = 'info';
  const leaseRejected = latestLeaseReview?.status === 'rejected';
  const leaseApproved = latestLeaseReview?.status === 'approved';
  if (leaseRejected) {
    alertType = 'warning';
  }
  if (leaseApproved) {
    alertType = 'success';
  }

  const EditAfterApprovedWarningModal = () => {
    return (
      <Modal
        headerContent={getTranslatedString(
          'landlordPortal.leaseCreatorV2.leaseSummaryPage.modalHeaderContent'
        )}
        bodyContent={getTranslatedString(
          'landlordPortal.leaseCreatorV2.leaseSummaryPage.modalBodyContent'
        )}
        warningModal={true}
        YesButtonContent={getTranslatedString(
          'landlordPortal.leaseCreatorV2.leaseSummaryPage.modalYesContent'
        )}
        NoButtonContent={getTranslatedString(
          'landlordPortal.leaseCreatorV2.leaseSummaryPage.modalNoContent'
        )}
        isMobile={isMobile}
        NoButtonAction={() => setShowEditModalWarning(false)}
        YesButtonAction={() => {
          requestLeaseReview();
          goTo(LEASE_DETAILS_PAGE);
        }}
      />
    );
  };

  const displayAlertDescription =
    latestLeaseReview && ['approved', 'rejected'].includes(latestLeaseReview.status);

  const reviewsOption = reviewers.map((reviewer) => {
    return {
      label: reviewer.full_name,
      value: reviewer.id,
    };
  });

  const renderLeaseApprovalContainer =
    application.status === PENDING_LEASE_APPROVAL && application.can_approve_leases;

  const renderLeaseRequestReviewer =
    application.status === CREATE_LEASE && latestLeaseReview?.status !== 'approved';

  return (
    <React.Fragment>
      {(restartWizard || ['approved', 'rejected'].includes(latestLeaseReview?.status)) &&
        showEditModalWarning && <EditAfterApprovedWarningModal />}
      <StyledContainer>
        <LeaseHeaderProgress
          lease={leaseDetails}
          stepsFilled={currentPage}
          totalSteps={totalPages}
        />

        <StyledPreviewContainer>
          <StyledPreview>
            <StyledPageHeader>
              {getTranslatedString('landlordPortal.leaseCreatorV2.leasePreviewPage.leasePreview')}
            </StyledPageHeader>
            {errors.approvalStatus && (
              <StyledAlert closable={false} message={errors.approvalStatus} type="error" />
            )}
            {errors.reviewerId && (
              <StyledAlert closable={false} message={errors.reviewerId} type="error" />
            )}
            {notice && notice !== '' && (
              <StyledAlert
                closable={false}
                message={
                  latestLeaseReview && (
                    <div>{`${getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leaseReview.status'
                    )}: ${notice}`}</div>
                  )
                }
                type={alertType}
                description={
                  displayAlertDescription && (
                    <div>
                      {latestLeaseReview.notes && (
                        <div>{`${getTranslatedString('notes')}: ${latestLeaseReview.notes}`}</div>
                      )}
                      {latestLeaseReview.reviewer_name && (
                        <div>
                          {getTranslatedString(
                            'landlordPortal.leaseCreatorV2.leaseReview.reviewedByOn',
                            {
                              name: latestLeaseReview.reviewer_name,
                              date: toFriendlyDateString(latestLeaseReview.created_at),
                            }
                          )}
                        </div>
                      )}
                    </div>
                  )
                }
              />
            )}
            {!isMobile ? (
              <React.Fragment>
                <StyledText>
                  {getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leasePreviewPage.downloadLease'
                  )}
                </StyledText>
                <Button
                  label={getTranslatedString(
                    'landlordPortal.leaseCreatorV2.leasePreviewPage.previewInHellosign'
                  )}
                  loading={fetchingHellosignUnclaimedDraft}
                  onClick={() => openPreview()}
                  size={isMobile ? 'large' : 'default'}
                />
                {previewError && (
                  <PreviewError>
                    {getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leaseReviewPage.previewErrorMessage'
                    )}
                  </PreviewError>
                )}
              </React.Fragment>
            ) : (
              <StyledUnavailablePreviewInfo>
                Leases cannot be previewed or sent using this screen size. Please use a device with
                a larger screen to complete the lease creation process.
              </StyledUnavailablePreviewInfo>
            )}

            {renderLeaseRequestReviewer && (
              <LeaseReviewerContainer>
                <Divider dashed={false} plain={false} orientation="" type="horizontal" />
                <VerticalFormWrapper style={{ width: '100%' }}>
                  <LeaseCreatorFormItem
                    label={getTranslatedString(
                      'landlordPortal.leaseCreatorV2.leasePreviewPage.leaseReviewer'
                    )}
                    labelSize={labelSize}
                    required
                  >
                    <StyledSelectContainer>
                      <StyledSelect
                        handleChange={(value) => setReviewerId(value)}
                        optionData={reviewsOption}
                        isMobile={isMobile}
                        error={errors.reviewerId}
                      />
                      <Button
                        type="primary"
                        htmlType={'button'}
                        label={getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leasePreviewPage.requestReview'
                        )}
                        onClick={() => {
                          requestLeaseReview();
                        }}
                        size={isMobile ? 'large' : 'default'}
                      />
                    </StyledSelectContainer>
                  </LeaseCreatorFormItem>
                </VerticalFormWrapper>
              </LeaseReviewerContainer>
            )}

            {renderLeaseApprovalContainer && (
              <React.Fragment>
                <Divider dashed={false} plain={false} orientation="" type="horizontal" />
                <LeaseApprovalContainer>
                  <VerticalFormWrapper>
                    <LeaseCreatorFormItem
                      label={
                        <StyledTitle>
                          {getTranslatedString(
                            'landlordPortal.leaseCreatorV2.leasePreviewPage.leaseApproval'
                          )}
                        </StyledTitle>
                      }
                      labelSize={labelSize}
                      required
                    />
                    <StyledRadioButton
                      radioGroupData={[
                        {
                          key: 1,
                          label: getTranslatedString(
                            'landlordPortal.leaseCreatorV2.leasePreviewPage.approved'
                          ),
                          value: LEASE_APPROVED,
                        },
                        {
                          key: 2,
                          label: getTranslatedString(
                            'landlordPortal.leaseCreatorV2.leasePreviewPage.rejected'
                          ),
                          value: LEASE_REJECTED,
                        },
                      ]}
                      checkedIndex={approvalStatus}
                      value={approvalStatus}
                      handleChange={(e) => {
                        setApprovalStatus(e.target.value);
                      }}
                      isMobile={isMobile}
                    />
                    <StyledTextAreaContainer>
                      <LeaseCreatorFormItem
                        label={getTranslatedString(
                          'landlordPortal.leaseCreatorV2.leasePreviewPage.notes'
                        )}
                        labelSize={labelSize}
                      >
                        <TextArea
                          value={notes}
                          rows={3}
                          placeholder={getTranslatedString(
                            'landlordPortal.leaseCreatorV2.leasePreviewPage.addNoteAboutTheLease'
                          )}
                          onChange={(event) => setNotes(event.currentTarget.value)}
                          isMobile={isMobile}
                        />
                      </LeaseCreatorFormItem>
                    </StyledTextAreaContainer>
                    <Button
                      type="primary"
                      htmlType={'button'}
                      label={getTranslatedString(
                        'landlordPortal.leaseCreatorV2.leasePreviewPage.submitReview'
                      )}
                      onClick={() => {
                        submitLeaseReview();
                      }}
                      size={isMobile ? 'large' : 'default'}
                    />
                  </VerticalFormWrapper>
                </LeaseApprovalContainer>
              </React.Fragment>
            )}

            {renderLeaseRequestReviewer && (
              <StyledRow>
                <Col>
                  <LeaseCreatorFormItem
                    label={
                      <StyledRequiredText>{getTranslatedString('required')}</StyledRequiredText>
                    }
                    labelSize={labelSize}
                    required
                  />
                </Col>
              </StyledRow>
            )}
          </StyledPreview>
          {leaseReviews && leaseReviews.length > 0 && (
            <LeaseReviewsContainer>
              <StyledTitle>
                {getTranslatedString('landlordPortal.leaseCreatorV2.leasePreviewPage.leaseReviews')}
              </StyledTitle>
              {leaseReviews.map((lease_review, index) => (
                <LeaseReview key={index} lease_review={lease_review} />
              ))}
            </LeaseReviewsContainer>
          )}
        </StyledPreviewContainer>
        <LeaseCreatorPageFooter
          forward={() => openPreview()}
          submitLoading={fetchingHellosignUnclaimedDraft}
          back={handleBackClick}
          currentPage={currentPage}
          totalPages={totalPages}
          handleCancellation={handleCancellation}
          disablePrimary={disablePrimaryButton()}
          restartWizard={
            restartWizard || ['approved', 'pending', 'rejected'].includes(latestLeaseReview?.status)
          }
          hidePrimaryButton={isMobile}
        />
      </StyledContainer>
    </React.Fragment>
  );
};

const mapStoreToProps = (store) => ({
  fetchingHellosignUnclaimedDraft: store.landlordSignableDoc.fetchingHellosignUnclaimedDraft,
  clientId: store.landlordSignableDoc.clientId,
  canApproveLeases: store.userTypes.canApproveLeases,
  latestLeaseReview: store.landlord.leaseCreatorV2.latestLeaseReview,
  leaseReviews: store.landlord.leaseCreatorV2.leaseReviews,
});

const mapDispatchToProps = {
  createHellosignUnclaimedDraft,
  storeLeaseReviews,
  storeLatestLeaseReview,
};

export default connect(mapStoreToProps, mapDispatchToProps)(LeasePreviewPage);
